import { useState } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { App, Badge, Button, Input, Modal, Tooltip } from 'antd';
import type { IERPUser } from '@admin-panel/types';
import {
  ArrowClockwiseIcon,
  BellIcon,
  BellRingingIcon,
  BellSlashIcon,
  LinkSimpleIcon,
  ShareNetworkIcon,
} from '@phosphor-icons/react';
import { useSharedHubAlertSetup } from '../hubAlertSetup/HubAlertSetupGate';
import { isHubAlertRecipient } from '../hubAlerts/hubAlertRules';
import {
  alertCount,
  ringingItems,
  useAlertCenter,
} from '../hubAlerts/alertCenter/alertCenterStore';
import { viewAlerts } from '../hubAlerts/alertCenter/alertCenterEffects';
import { keepFocus } from '../hubAlerts/alertCenter/alertFormat';
import { AlertCenterBoundary } from '../hubAlerts/alertCenter/AlertCenterBoundary';
import { readDevice } from '../hubAlertSetup/useHubAlertSetup';
import { isInstalledWindow } from '../hubAlerts/hubWindowMode';
import { shareMethod, shareableUrl } from './shareLink';

const ICON_SIZE = 20;

/**
 * Copies (computer) or shares (Android share sheet) the page's link. The installed app has no
 * address bar, and its Copy URL sits inside a menu.
 */
const ShareLinkButton = () => {
  const { message } = App.useApp();
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const method = shareMethod(readDevice().platform, typeof navigator.share === 'function');

  const onClick = async () => {
    const url = shareableUrl(window.location.href);
    if (method === 'share') {
      try {
        await navigator.share({ url });
        return;
      } catch (error) {
        // Closed the share sheet: nothing to do. Anything else: copy instead.
        if ((error as DOMException)?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      message.success('Link copied');
    } catch {
      // No clipboard access (browser setting or an old browser): show the link to copy by hand.
      setFallbackLink(url);
    }
  };

  const label = method === 'share' ? 'Share link to this page' : 'Copy link to this page';
  return (
    <>
      <Tooltip title={label}>
        <Button
          type="text"
          shape="circle"
          aria-label={label}
          icon={
            method === 'share' ? (
              <ShareNetworkIcon size={ICON_SIZE} />
            ) : (
              <LinkSimpleIcon size={ICON_SIZE} />
            )
          }
          onClick={() => void onClick()}
        />
      </Tooltip>
      <Modal
        open={fallbackLink !== null}
        title="Link to this page"
        okText="Done"
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => setFallbackLink(null)}
        onCancel={() => setFallbackLink(null)}
      >
        <Input
          readOnly
          autoFocus
          value={fallbackLink ?? ''}
          onFocus={event => event.target.select()}
        />
      </Modal>
    </>
  );
};

/**
 * The bell: today's order alerts and what is waiting (opens the alerts drawer). The badge counts
 * alerts not yet looked at, a dot marks a new waiting list, and the icon's colour still tells this
 * device's alert setup at a glance (green on, amber browser tab, red not set up).
 */
const AlertBellButton = () => {
  const { data: user } = useGetIdentity<IERPUser>();
  const setup = useSharedHubAlertSetup();
  const center = useAlertCenter();
  if (!isHubAlertRecipient(user?.roles)) return null;

  const count = alertCount(center);
  const ringing = ringingItems(center).length > 0;
  const covered = setup.applies && setup.mode !== 'off' && setup.settled;
  const missing = covered && setup.step !== null;
  const inTab = covered && setup.step === null && !setup.installed;
  const setupText = missing
    ? 'Order alerts are not set up on this device'
    : inTab
      ? 'Alerts reach this device; open Hub from its app icon to work'
      : covered
        ? 'Order alerts are on for this device'
        : null;
  const label = [count > 0 ? `Alerts, ${count} new` : 'Alerts', setupText]
    .filter(Boolean)
    .join('. ');
  const color = ringing
    ? '#D92D20'
    : missing
      ? '#B42318'
      : inTab
        ? '#DC6803'
        : covered
          ? '#079455'
          : undefined;
  const icon = missing ? (
    <BellSlashIcon size={ICON_SIZE} weight="fill" color={color} />
  ) : ringing || count > 0 ? (
    <BellRingingIcon size={ICON_SIZE} weight="fill" color={color} />
  ) : (
    <BellIcon size={ICON_SIZE} weight={covered ? 'fill' : 'regular'} color={color} />
  );
  return (
    <Tooltip title={label}>
      <Badge count={count} size="small" dot={count === 0 && center.waitingUnseen} offset={[-4, 4]}>
        <Button
          type="text"
          shape="circle"
          aria-label={label}
          icon={icon}
          // The drawer hands focus back to the task (scan box) on close, not to the bell.
          data-hub-alert-ui
          onMouseDown={keepFocus}
          onClick={() => viewAlerts()}
        />
      </Badge>
    </Tooltip>
  );
};

/** The installed app on a phone has no browser controls to reload a stuck page. */
const ReloadButton = () => (
  <Tooltip title="Reload Hub">
    <Button
      type="text"
      shape="circle"
      aria-label="Reload Hub"
      icon={<ArrowClockwiseIcon size={ICON_SIZE} />}
      onClick={() => window.location.reload()}
    />
  </Tooltip>
);

/** Hub's controls in the top bar of every page (desktop header and mobile top bar). */
const HubHeaderTools = () => {
  const [showReload] = useState(() => isInstalledWindow() && readDevice().platform === 'android');
  return (
    <span className="hub-header-tools" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <AlertCenterBoundary>
        <AlertBellButton />
      </AlertCenterBoundary>
      <ShareLinkButton />
      {showReload && <ReloadButton />}
    </span>
  );
};

export default HubHeaderTools;
